import { useEffect, useMemo, useState } from 'react';
import { BREAKPOINTS } from '@/design-tokens';

/** Union of all available breakpoint token keys (e.g. `'sm'`, `'md'`, `'lg'`, etc.) */
export type BreakpointKey = keyof typeof BREAKPOINTS;

/**
 * Controls which side of the media query is tested:
 * - `'min'` → `min-width` — matches when the viewport is **at or above** the breakpoint.
 * - `'max'` → `max-width` — matches when the viewport is **at or below** the breakpoint.
 */
export type ResponsiveDirection = 'min' | 'max';

/**
 * Input options for `useResponsive`.
 */
export interface ResponsiveCheckOptions {
  /** Breakpoint token key to check against */
  breakpoint: BreakpointKey;
  /** Direction of the media query check ('min' for >=, 'max' for <=) */
  direction?: ResponsiveDirection;
  /** Optional initial value for SSR/hydration */
  initialValue?: boolean;
}

/**
 * Responsive helper hook for breakpoint checks using design tokens.
 *
 * Builds a `min-width` or `max-width` media query string from the given
 * breakpoint token and subscribes to viewport changes via the
 * `MediaQueryList` API. The returned boolean re-renders the component
 * whenever the viewport crosses the threshold.
 *
 * SSR / hydration:
 * - On the server (`window === undefined`) the hook cannot call `matchMedia`,
 *   so it falls back to `initialValue` (default `false`).
 * - On the client the hook reads `matchMedia.matches` immediately and then
 *   keeps it in sync via a `change` event listener.
 *
 * ```tsx
 * // example
 * const isXlUp = useResponsive({ breakpoint: 'xl', direction: 'min' });
 * const isLgDown = useResponsive({ breakpoint: 'lg', direction: 'max' });
 * ```
 */
export function useResponsive({
  breakpoint,
  direction = 'min',
  initialValue = false,
}: ResponsiveCheckOptions): boolean {
  // ================================
  // Query string
  // ================================
  /**
   * Build the CSS media query string from the design-token pixel value and
   * the requested direction.
   *
   * Examples:
   * - `breakpoint='lg', direction='min'` → `"(min-width: 1024px)"`
   * - `breakpoint='md', direction='max'` → `"(max-width: 768px)"`
   *
   * Memoized so the string reference is stable between renders and the
   * `useEffect` / `useState` below only re-run when the inputs actually change.
   */
  const query = useMemo(() => {
    const width = BREAKPOINTS[breakpoint];
    return `(${direction}-width: ${width})`;
  }, [breakpoint, direction]);

  // ================================
  // Matches state
  // ================================
  /**
   * Tracks whether the current viewport satisfies the media query.
   *
   * The lazy initializer runs once on mount:
   * - In SSR contexts (`window === 'undefined'`) it returns `initialValue` so
   *   the server-rendered markup stays stable until hydration.
   * - In browser contexts it reads the current `matches` value immediately so
   *   there is no flash of incorrect state after hydration.
   */
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === 'undefined') return initialValue;
    return window.matchMedia(query).matches;
  });

  // ================================
  // Media query listener
  // ================================
  /**
   * Subscribes to the `change` event on the `MediaQueryList` so the component
   * re-renders whenever the viewport crosses the breakpoint threshold.
   *
   * Lifecycle:
   * 1. Bail out on the server (no `window`).
   * 2. Create a `MediaQueryList` for the current query string.
   * 3. Sync `matches` immediately (handles the case where the query changed
   *    after the initial render, e.g. a dynamic breakpoint prop).
   * 4. Bail out if `addEventListener` is not supported (very old browsers).
   * 5. Attach the listener and return a cleanup function that removes it.
   *
   * Re-runs whenever `query` changes, which itself changes only when
   * `breakpoint` or `direction` changes.
   */
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueryList = window.matchMedia(query);

    // Handler called by the browser whenever the query match state changes.
    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Sync immediately in case the query changed between renders.
    setMatches(mediaQueryList.matches);

    // Guard: `addEventListener` may not exist in very old browser environments.
    if (!mediaQueryList.addEventListener) return;

    mediaQueryList.addEventListener('change', handleChange);

    // Cleanup: remove the listener when the effect is torn down.
    return () => mediaQueryList.removeEventListener('change', handleChange);
  }, [query]);

  // ================================
  // Return
  // ================================
  /** `true` when the current viewport satisfies the media query, `false` otherwise. */
  return matches;
}
